import { useEffect } from "react";

/**
 * Drives the overlay scrollbar defined in `customScrollbar.css`.
 *
 * Every `.custom-scrollbar-overlay` element on the page gets a
 * `scrollbar-visible` class while it is hovered, scrolled, or being dragged;
 * the class is what makes the thumb fade in. A MutationObserver re-attaches
 * to nodes that mount later (modals, dropdowns, route changes), so calling
 * this once in the layout covers the whole app.
 */
const useCustomScrollbar = () => {
  useEffect(() => {
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    let isMouseDown = false;

    const show = (el: Element) => {
      clearTimeout(timers.get(el));
      el.classList.add("scrollbar-visible");
    };

    const hide = (el: Element, delay = 100) => {
      clearTimeout(timers.get(el));
      timers.set(
        el,
        setTimeout(() => {
          if (!isMouseDown) {
            el.classList.remove("scrollbar-visible");
          }
        }, delay),
      );
    };

    const attach = (el: Element) => {
      // Guard against double-attaching
      if (el.getAttribute("data-scrollbar-attached")) return;
      el.setAttribute("data-scrollbar-attached", "true");

      el.addEventListener("mouseenter", () => show(el));
      el.addEventListener("mouseleave", () => hide(el));
      el.addEventListener("mousedown", () => {
        isMouseDown = true;
        show(el);
      });
      el.addEventListener("scroll", () => show(el));
    };

    const attachAll = () => {
      document.querySelectorAll(".custom-scrollbar-overlay").forEach(attach);
    };

    // When the mouse is released anywhere on the page, allow hiding again
    const onMouseUp = () => {
      isMouseDown = false;
      document.querySelectorAll(".custom-scrollbar-overlay").forEach((el) => {
        if (!el.matches(":hover")) {
          hide(el, 100);
        }
      });
    };

    document.addEventListener("mouseup", onMouseUp);

    attachAll();

    const observer = new MutationObserver(attachAll);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      observer.disconnect();
      timers.forEach((t) => clearTimeout(t));
      document.querySelectorAll("[data-scrollbar-attached]").forEach((el) => {
        el.removeAttribute("data-scrollbar-attached");
      });
    };
  }, []);
};

export default useCustomScrollbar;
